import axios, {AxiosRequestConfig} from "axios";
import {Link} from './Client';
import {LOCALTOKENKEY, PPNTOKEN} from "./BaseClient";

/** All supported request methods. */
type RequestMethod = "get" | "post" | "patch" | "delete";

/** Collection of relevant information of each HATEOAS request. */
class RequestInfo {
  /**
   * @param _method the axios HTTP method to send a request as
   * @param _status all valid HTTP status codes for the request
   */
  constructor(private _method: RequestMethod, private _status: number[]) {
  }

  public get method(): RequestMethod {
    return this._method;
  }

  public get status(): number[] {
    return this._status;
  }

  /**
   * Checks if the given HTTP status code signals success or failure.
   *
   * @param status the HTTP status received from the back end for the request
   * @returns true if the status code signals 'success' for the request, false otherwise
   */
  public isExpectedStatus = (status: number): boolean => (
    this.status.indexOf(status) > -1
  )
}

export class RequestInfos {
  public static get = new RequestInfo("get", [200]);
  public static post = new RequestInfo("post", [201, 204]);
  public static patch = new RequestInfo("patch", [200, 204]);
  public static delete = new RequestInfo("delete", [200, 202, 204]);

  /**
   * Retrieve the request info from a given request method.
   * The method throws for unexpected method input.
   *
   * @param method the request method, e.g. "get"
   * @returns the request info for the method
   */
  public static fromRequestMethod = (method: RequestMethod): RequestInfo => (
    RequestInfos[method]
  )

  /**
   * Retrieve the request info from a given verb.
   * The method throws for unexpected verb input.
   *
   * @param verb the API name, e.g. "POST_user"
   * @returns the request info for the verb
   */
  public static fromVerb = (verb: string): RequestInfo => (
    RequestInfos[verb.split("_")[0].toLowerCase()]
  )
}

/**
 * Simple data structure to combine request method and url. Useful
 * because both properties are extracted and used from *Dto objects.
 */
export interface RequestProps {
  info: RequestInfo;
  url: string;
}

/**
 * Apply neccessary transformation to a response received from the back end.
 *
 * @param data the data object of the axios response to be transformed
 * @returns the transformed object
 */
const prepareResponseData = <TResult>(data: any): TResult => {
  const objStr = JSON.stringify(data);
  const preparedStr = objStr.replace(new RegExp('"_links":{', 'g'), '"links":{');
  return JSON.parse(preparedStr) as TResult;
};

/**
 * Make a REST request and return the resulting promise.
 * @param baseUrl 'baseURL' property for the underlying axios call
 * @param token the authentication token to be used for the call
 * @param linkProps the request info and url
 * @param data the object to be sent with the request or undefined if none
 */
export const call = <TResult>(baseUrl: string,
                              token: string,
                              requestProps: RequestProps,
                              data?: any): Promise<TResult> => (
    new Promise(async (resolve, reject) => {
      const config: AxiosRequestConfig = {
        baseURL: baseUrl,
        url: requestProps.url,
        method: requestProps.info.method,
        responseType: "json",
        timeout: 30 * 60 * 1000,
        data: data,
        headers: {
          authorization: token ? "Bearer " + token : undefined,
          "X-Forwarded-Ssl": "on",
          "PROMT-Host": window.location.hostname
        },
      };
      const ppnToken = sessionStorage.getItem(PPNTOKEN);
      if(ppnToken) {
        config.headers.PpnToken = ppnToken;
      }
      try {
        const response = await axios(config);
        if (requestProps.info.isExpectedStatus(response.status)) {
          resolve(prepareResponseData<TResult>(response.data));
        } else {
          return reject(response.statusText);
        }
      } catch (error) {
        if (error && error.response && error.response.status === 401) {
          localStorage.clear();
          sessionStorage.clear();
          return location.reload();
        }
        return reject(error);
      }
    })
  );

/**
 * Check whether an object has a given property.
 *
 * @param obj the object to check
 * @param prop the expected property
 * @returns true if the property exists in the object, false otherwise
 */
const hasProperty = (obj: any, prop: string): boolean => (
  obj !== undefined && prop in obj
);

/**
 * Ensure an object has a given property.
 * Throws an error in case the property doesn't exist.
 *
 * @param obj the object to check
 * @param prop the expected property
 */
const requireProperty = (obj: any, prop: string): void => {
  if (!hasProperty(obj, prop)) {
    throw new Error("Failed to find required property " + prop + " inside object " + JSON.stringify(obj));
  }
};

/**
 * checks if the object has a property 'href'.
 * @param obj object to be check if it's a link
 */
const isLink = (obj: object): obj is Link => {
  return hasProperty(obj, 'href');
};

/**
 * Check whether an object contains links to execute a given verb.
 * The object is usually any type of *Dto that contains a "links" property.
 *
 * @param verb the API name, e.g. "POST_user"
 * @param linkContainer the object to check for the verb
 * @returns true if a link for the verb is found, false otherwise
 */
export const canExecute = (verb: string, linkContainer: any): boolean => {
  if (linkContainer === undefined) {
    return false;
  }

  if (isLink(linkContainer)) {
    const link = linkContainer as Link;
    return link.href!.length > 0 && link.rel === verb;
  }

  /* tslint:disable:no-string-literal */
  return hasProperty(linkContainer, "links") && hasProperty(linkContainer["links"], verb);
  /* tslint:enable:no-string-literal */
};

/**
 * Extract the requestinfo and the url for a verb of a given object.
 * This function throws for unexpected input. The client is required
 * to check the input before making the call.
 *
 * @param verb the API name, e.g. "POST_user"
 * @param linkContainer an object that contains links for the given verb
 * @param data an object that contains data to be added if the link is templated
 * @returns an object consisting of request method and url
 */
export const getRequestProps = (verb: string, linkContainer: object, data?: any): RequestProps => {
  let link;
  if (isLink(linkContainer)) {
    link = Object.assign({}, linkContainer);
    if (link.rel !== verb) {
      throw new Error("The link object " + JSON.stringify(link) + " doesn't have correct relationship " + verb);
    }
  } else {
    /* tslint:disable:no-string-literal */
    requireProperty(linkContainer, "links");
    const links = linkContainer["links"] as Link[];

    requireProperty(links, verb);
    link = Object.assign({}, links[verb]) as Link;
  }

  requireProperty(link, "href");
  const pattern = new RegExp("\\{?\\?.*$");
  link.href = link.href!.replace(pattern, "");

  if (link.templated === true && data && typeof data === 'object') { // process templates
    const queryString = Object.keys(data).map(key => key + '=' + data[key]).join('&'); // TODO: use encodeURIComponent by strings
    link.href = link.href + '?' + queryString;
  }

  return {info: RequestInfos.fromVerb(verb), url: link.href};
  /* tslint:enable:no-string-literal */
};



// WEBPACK FOOTER //
// ./src/services/HATEOASUtils.ts